<template>
  <div>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="mb-1 d-flex align-items-center justify-content-start mb-md-0"
        >
          <label>Show</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="mr-1 per-page-selector d-inline-block ml-50"
          />
          <b-button v-b-modal.add-serviceType variant="primary"
            >Add Service Type</b-button
          >
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          class="mb-1 d-flex align-items-center justify-content-end mb-md-0"
        >
          <label class="mr-1">
            Search
          </label>
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              class="mr-1 d-inline-block"
              placeholder="Search..."
              v-model="searchWord"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <!-- Larger screen than mobile -->
    <div class="d-none d-md-flex">
      <b-table
        class="border"
        no-local-sorting
        @sort-changed="sortingChanged"
        responsive
        striped
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :items="serviceType"
        :fields="serviceTypeFields"
      >
        <template #cell(action)="serviceType">
          <div class="d-flex justify-content-between">
            <div class="d-flex">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="secondary"
                class="mr-1 btn-icon"
                @click="editModal(serviceType.item.id, serviceType.item.name, serviceType.item.attachment, serviceType.item.category, serviceType.item.source)"
              >
                <feather-icon icon="Edit3Icon" />
              </b-button>
              <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="danger"
                  class="mr-1 btn-icon"
                  @click="showMsgBoxDelete(serviceType.item.id)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
            </div>
          </div>
        </template>
        <template #cell(attachment)="serviceType">
          <div class="d-flex justify-content-between">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="btn-icon mr-1"
              @click="previewImg(serviceType.item.name, serviceType.item.attachment, serviceType.item.category)"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>
          </div>
        </template>
      </b-table>
    </div>
    <!-- Mobile screen -->
    <div class="d-flex d-md-none flex-column">
      <!-- Loading -->
      <div v-if="isBusy" class="text-center text-danger my-2">
        <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
        <strong class="text-secondary">Loading...</strong>
      </div>
      <!-- Data -->
      <div v-if="!isBusy && serviceType.length == 0" class="text-center text-danger my-2">
        <strong class="text-secondary">No data</strong>
      </div>
      <b-row
        v-if="!isBusy && serviceType.length > 0"
        v-for="(item, index) in serviceType"
        :key="item.id"
        class="mb-3 list-item"
      >
        <b-col cols="12" class="d-flex flex-wrap justify-content-end align-items-center mb-1" style="gap: 1rem;">
          <strong class="flex-grow-1 truncate">{{ item.name }}</strong>
          <div>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="btn-icon mr-1"
              @click="previewImg(item.name, item.attachment, item.category)"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="secondary"
              class="mr-1 btn-icon"
              @click="editModal(item.id, item.name, item.attachment, item.category, item.source)"
            >
              <feather-icon icon="Edit3Icon" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="danger"
              class="mr-1 btn-icon"
              @click="showMsgBoxDelete(item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </b-col>
        <b-col cols="12" class="d-flex flex-wrap">
          <div style="font-size: 0.75rem;">
            <p><strong>CATEGORY:</strong> {{ item.category || '-' }}</p>
            <p><strong>SOURCE:</strong> {{ item.source || '-' }}</p>
          </div>
        </b-col>
      </b-row>
    </div>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">
            Showing {{ pagination.from }} to {{ pagination.to }} of
            {{ pagination.total }} entries
          </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="pagination.total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
            @input="changePage"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
    <b-modal
      title="Add Service Type"
      hide-footer
      id="add-serviceType"
      centered
      hide-header-close
      no-close-on-backdrop
      @shown="initDropzone"
    >
      <b-form @submit.prevent="addServiceType">
        <b-form-group label-for="value">
          <template slot="label">
            <span>Name <strong class="text-danger">*</strong></span>
          </template>
          <b-form-input id="title" required v-model="serviceType_name" />
        </b-form-group>
        <b-form-group label-for="value">
          <template slot="label">
            <span>Category <strong class="text-danger">*</strong></span>
          </template>
          <v-select
            v-model="category"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            key="id"
            label="category"
            :options="categoryOpt"
          />
        </b-form-group>
        <b-form-group label-for="value">
          <template slot="label">
            <span>Source</span>
          </template>
          <b-form-input id="source" v-model="serviceType_source" />
        </b-form-group>
        <b-form-group label-for="value">
          <template slot="label">
            <span>Attachment </span>
          </template>
          <!-- <b-form-input id="dropzone-service-type-attachment" required v-model="serviceType_name" /> -->
          <div id="dropzone-service-type-attachment" class="dropzone dropzone-area mb-3">
            <div class="dz-message">
              Drop files here or click button to upload. <br />(Max. size 2MB)
            </div>
          </div>
        </b-form-group>
        <b-form-group class="d-flex justify-content-end">
          <b-button
            type="button"
            variant="light"
            class="mr-1"
            @click="$bvModal.hide('add-serviceType')"
            :disabled="form.isLoading"
          >
            Cancel
          </b-button>
          <b-button type="submit" variant="primary" :disabled="form.isLoading">
            Save
          </b-button>
        </b-form-group>
      </b-form>
    </b-modal>

    <b-modal
      title="Preview Image"
      hide-footer
      id="preview-image"
      centered
      hide-header-close
    >
      <b-form>
        <div class="text-center">
          <img
            :src="previewImageSrc"
            alt="Preview"
            class="img-fluid"
            v-if="previewImageSrc"
          />
          <p v-else>No Image Available</p>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      title="Edit Service Type"
      hide-footer
      id="edit-service-type"
      centered
      hide-header-close
      no-close-on-backdrop
      @shown="initDropzone"
      
    >
      <b-form @submit.prevent="editServiceType">
        <b-form-group label-for="value">
          <template slot="label">
            <span>Name <strong class="text-danger">*</strong></span>
          </template>
          <b-form-input id="title" required v-model="edit_serviceType_name" />
        </b-form-group>
        <b-form-group label-for="value">
          <template slot="label">
            <span>Category <strong class="text-danger">*</strong></span>
          </template>
          <v-select
            v-model="edit_serviceType_category"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            key="id"
            label="category"
            :options="categoryOpt"
          />
          
        </b-form-group>
        <b-form-group label-for="value">
          <template slot="label">
            <span>Source</span>
          </template>
          <b-form-input id="title" v-model="edit_serviceType_source" />
        </b-form-group>
        <b-form-group label-for="value">
          <template slot="label">
            <span>Attachment <strong class="text-danger">*</strong></span>
          </template>
          <!-- <b-form-input id="dropzone-service-type-attachment" required v-model="serviceType_name" /> -->
          <div id="dropzone-service-type-attachment" class="dropzone dropzone-area mb-3">
            <div class="dz-message">
              Drop files here or click button to upload. <br />(Max. size 2MB)
            </div>
          </div>
        </b-form-group>
        <b-form-group class="d-flex justify-content-end">
          <b-button
            type="button"
            variant="light"
            class="mr-1"
            @click="$bvModal.hide('edit-service-type')"
            :disabled="form.isLoading"
          >
            Cancel
          </b-button>
          <b-button type="submit" variant="primary" :disabled="form.isLoading">
            Save
          </b-button>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- <b-modal hide-footer title="Participants" size="xl" id="participant-modal">
      <participant-pcc-table :pccId="selectedPCC" />
    </b-modal> -->
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import VSelect from 'vue-select'
import {
  BRow,
  BCol,
  BTable,
  BFormCheckbox,
  BButton,
  BSpinner,
  BForm,
  BFormGroup,
  BFormInput,
  BPagination,
  BModal,
  VBModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
// import ParticipantPccTable from '@/views/admin/pcc/ParticipantPCCTable'
import { generateToken } from '@/auth/helperAuth'

import { ref  } from 'vue'


export default {
  
  components: {
    BTable,
    BFormCheckbox,
    BButton,
    BSpinner,
    BRow,
    BCol,
    VSelect,
    BForm,
    BFormGroup,
    BFormInput,
    BPagination,
    BModal,
    // ParticipantPccTable,
  },

  directives: {
    Ripple,
    VBModal,
  },

  data() {
    return {
      token: '',
      sortBy: 'start_datetime',
      sortDesc: true,
      isBusy: false,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 20, 30],
      pagination: {
        from: 1,
        to: 10,
        total: 0,
      },
      serviceType: [],
      serviceTypeFields: [
        { key: 'name', label: 'Service Name' },
        { key: 'category', label: 'Category' },
        { key: 'source', label: 'Source' },
        { key: 'attachment', label: 'Attachment' },
        { key: 'action', label: 'Action' },
      ],
      selectedPCC: '',
      searchWord: '',
      serviceType_name: '',
      serviceType_source: '',
      edit_serviceType_id: '',
      edit_serviceType_name: '',
      edit_serviceType_source: '',
      edit_serviceType_category: '',
      edit_serviceType_attachment: '',
      category:'',
      categoryOpt:[
        { id: 1, category: 'TELKOM' },
        { id: 2, category: 'KOMPETITOR' },
      ],
      attachment: '',
      dzInstance: null,
      previewImageSrc: '', 
      form: {
        isLoading: false 
      },
    }
  },
  created() {
    const payload = {
      client: "product-catalog-fe",
      kode: this.$store.getters['oauth/getActiveUser'].id.toString(),
      user: this.$store.getters['oauth/getActiveUser'].name,
      role: this.$store.getters['oauth/getActiveUser'].role,
    };
    this.token = generateToken(payload)
  },

  watch: {
    perPage() {
      this.fetchServiceType()
    },
    currentPage() {
      this.fetchServiceType()
    },
    searchWord(newVal) {
      this.searchByTitle()
    },
  },

  mounted() {
    this.fetchServiceType()
  },

  // mounted() {
  //   this.initDropzone()
  // },

  // destroyed() {
  //   this.dzInstance.disable()
  // },

  methods: {

    fetchServiceType() {
      this.isBusy = true
      
      let params = {
        table: "service_type",
        page: this.currentPage,
        perPage: this.perPage,
        order: 'DESC'
      }
      if (this.searchWord) {
        params.search = this.searchWord.toString().trim()
      }

      this.$http.get(`${process.env.VUE_APP_API_HELPER_URL}/internal/data/list`, {
        params: params,
        withCredentials: true,
        headers: { 'Client-Device': 'browser', 'Authorization': `${this.token}` },
      })
      .then(({ data }) => {
        console.log(data)
        this.currentPage = data.current_page
        this.pagination.total = data.total
        this.pagination.from = data.from
        this.pagination.to = data.to
        this.serviceType = data.data
      })
      .finally(() => {
        this.isBusy = false
      })

      
    },

    searchByTitle: debounce(function() {
      this.fetchServiceType()
    }, 200),

    changePage(page) {
      this.currentPage = page
    },

    sortingChanged({ sortBy, sortDesc }) {
      ;(this.sortBy = sortBy), (this.sortDesc = sortDesc)
      this.fetchServiceType()
    },

    showMsgBoxDelete(id) {
      this.$bvModal
        .msgBoxConfirm('Please confirm that you want to delete this item.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.deleteService(id)
              .then(() => {
                this.$notif(
                  'Success',
                  'PCC has been successfully deleted.',
                  'success'
                )
                this.fetchServiceType()
              })
              .catch(err => {
                this.$notif('Error', `${err.message}`, 'error')
              })
          }
        })
    },

    addServiceType() {
      this.form.isLoading = true
      const formData = new FormData()
      formData.append('name', this.serviceType_name)
      formData.append('category', this.category.category)
      formData.append('source', this.serviceType_source)
      if(this.attachment !== ''){
        formData.append('attachment', this.attachment, this.attachment.name)
      }

      this.$http
        .post(`${process.env.VUE_APP_API_HELPER_URL}/competitor`, formData, {
          withCredentials: true,
          headers: { 
            'Client-Device': 'browser',
            'Content-Type': 'multipart/form-data',
            'Authorization': `${this.token}`,
          },
        })
        .then((response) => {
          this.$notif('Success', 'Service Type successfully added.', 'success')
          this.$bvModal.hide('add-serviceType')
          this.serviceType_name = ''
          this.category = null
          this.attachment = null
          this.fetchServiceType()
        })
        .catch((error) => {
          console.error('Error:', error)
          this.$notif('Error', error.response?.data?.message || 'Upload failed', 'error')
        })
        .finally(() => {
          this.form.isLoading = false
        })
    },

    editServiceType() {
      this.form.isLoading = true

      const formData = new FormData()
      formData.append('id', this.edit_serviceType_id)
      formData.append('name', this.edit_serviceType_name)
      formData.append('source', this.edit_serviceType_source)
      formData.append('category', this.edit_serviceType_category.category ? this.edit_serviceType_category.category : this.edit_serviceType_category)
      if (this.edit_serviceType_attachment) {
        formData.append('attachment', this.edit_serviceType_attachment, this.edit_serviceType_attachment.name)
      }
      this.$http
        .put(`${process.env.VUE_APP_API_HELPER_URL}/competitor`, formData, {
          withCredentials: true,
          headers: { 
            'Client-Device': 'browser',
            'Content-Type': 'multipart/form-data',
            'Authorization': `${this.token}`,
          },
        })
        .then((response) => {
          console.log('Response:', response)
          this.$notif('Success', 'Edit Service Type successfully.', 'success')
          this.$bvModal.hide('edit-service-type')
          this.edit_serviceType_name = ''
          this.edit_serviceType_source = ''
          this.edit_serviceType_category = null
          this.edit_serviceType_attachment = null
          this.fetchServiceType()
        })
        .catch((error) => {
          console.error('Error:', error)
          this.$notif('Error', error.response?.data?.message || 'Upload failed', 'error')
        })
        .finally(() => {
          this.form.isLoading = false
        })
    },

    async previewImg(name, path, category){
      try {
        const response = await this.$http.get(`${process.env.VUE_APP_API_HELPER_URL}/attachment?path=${path}`, {
          headers:{
          'Authorization': `${this.token}`
          },
          responseType: 'blob',
        })
        this.previewImageSrc = URL.createObjectURL(response.data)
      } catch (error) {
        console.error("Gagal memuat gambar KOMPETITOR:", error)
      }
      this.$bvModal.show('preview-image')
    },

    editModal(id,name, path,category, source){
      this.edit_serviceType_id = id
      this.edit_serviceType_name = name
      this.edit_serviceType_category = category
      this.edit_serviceType_source = source
      this.$bvModal.show('edit-service-type')
    },

    async deleteService(id) {
      this.isBusy = true
      return await this.$http
        .delete(`${process.env.VUE_APP_API_HELPER_URL}/internal/data?table=service_type&id=${id}`)
        .then(res => {
          this.$notif(
            'Success',
            'News has been successfully deleted.',
            'success'
          )
        })
        .catch(({ data }) => {
          this.$notif('Something Error', data.message, 'danger')
        })
        .finally(() => {
          this.isBusy = false
        })
    },

    initDropzone() {
      const dropzoneElement = document.querySelector('#dropzone-service-type-attachment');
      if (!dropzoneElement) {
        console.error('Dropzone element not found');
        return;
      }
      const options = {
        url: '#',
        maxFilesize: 2000,
        maxFiles: 1, // Batasi hanya satu file
        autoProcessQueue: false, // Tidak otomatis mengunggah
        autoQueue: false,
        addRemoveLinks: true,
        acceptedFiles: 'image/jpeg,image/jpg',
      };

      const { drawLogoImage, deleteTempFile, handleFileAdded} = this

      this.dzInstance = new window.Dropzone(dropzoneElement, {
        ...options,
        init() {
          this.on('addedfile', (file) => handleFileAdded(file)); // Tangkap file
          this.on('success', (file, res) => drawLogoImage(res.data));
          this.on('removedfile', () => deleteTempFile());
        },
      })
    },

    handleFileAdded(file) {
      // Simpan file ke variabel untuk digunakan saat submit
      this.attachment = file;
      this.edit_serviceType_attachment = file;
      console.log('File ditambahkan:', file);
    },

    drawLogoImage(imagePath) {
      console.log(imagePath, "AAAAAAAAA")
      this.form.image = imagePath
    },

    deleteTempFile() {
      // Check if there is a file to remove
      if (this.attachment) {
        // Remove the file from Dropzone
        this.dzInstance.removeFile(this.attachment);
        // Reset the attachment variable
        this.attachment = null; 
        console.log('File has been removed from Dropzone');
      } else {
        console.log('No file to remove');
      }
    }
    
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
